/**
 * Error Log Page
 * Visar alla systemfel och exceptions.
 * Lista med rubrik per rad; klick öppnar detaljvy (modal) med full felinfo och status-uppdatering.
 */

import { useState, useCallback } from 'react';
import { ROUTES } from '@/constants/routes';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/store/languageStore';
import { ArrowLeftIcon, ExclamationCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAsyncData, usePagination, useApiCall } from '@/hooks';
import {
 ErrorLogEntry,
 ErrorLogFilters,
} from '@/services/errorLog/errorLogService';

const TITLE_MAX_LEN = 100;

/** Rubrik för listrad: första raden eller trunkerat meddelande */
function getErrorTitle(message: string): string {
 const firstLine = message.split(/\r?\n/)[0]?.trim() ?? message;
 if (firstLine.length <= TITLE_MAX_LEN) return firstLine;
 return firstLine.slice(0, TITLE_MAX_LEN) + '...';
}

const ErrorLog = () => {
 const navigate = useNavigate();
 const { t } = useTranslation();
 const [filters, setFilters] = useState<ErrorLogFilters>({
 severity: undefined,
 status: undefined,
 search: '',
 });
 const [detailLog, setDetailLog] = useState<ErrorLogEntry | null>(null);
 const [detailStatus, setDetailStatus] = useState<'NEW' | 'INVESTIGATING' | 'RESOLVED' | 'IGNORED' | ''>('');
 const [detailResolutionNotes, setDetailResolutionNotes] = useState('');
 const [showClearDialog, setShowClearDialog] = useState(false);
 const [clearOlderThan, setClearOlderThan] = useState<string>('');
 const [clearing, setClearing] = useState(false);

 const pagination = usePagination({ initialPageSize: 100 });
 const { execute: updateStatus } = useApiCall();

 // Hämta error logs med useAsyncData
 // Backend returnerar antingen { data: { data: logs[], pagination } } (nytt) eller { data: logs[] } (gammalt)
 const { data: logsData, loading, refetch } = useAsyncData<{ data: ErrorLogEntry[]; pagination?: { total: number } } | ErrorLogEntry[]>(
 '/api/sysadmin/error-log',
 {
 params: {
 ...filters,
 limit: pagination.pageSize,
 offset: pagination.offset,
 },
 deps: [filters, pagination.page, pagination.pageSize],
 onSuccess: (result) => {
 const res = result as { data?: ErrorLogEntry[]; pagination?: { total: number } };
 if (res?.pagination?.total !== undefined) {
 pagination.setTotal(res.pagination.total);
 }
 },
 }
 );

 const logs = Array.isArray(logsData) ? logsData : (logsData?.data ?? []);

 // Hämta statistik
 const { data: statistics } = useAsyncData<any>('/api/sysadmin/error-log/statistics');

 const handleFilterChange = useCallback((key: keyof ErrorLogFilters, value: any) => {
 setFilters(prev => ({ ...prev, [key]: value }));
 pagination.firstPage();
 }, [pagination]);

 const handleStatusUpdate = useCallback(async (
 errorId: number,
 status: 'NEW' | 'INVESTIGATING' | 'RESOLVED' | 'IGNORED',
 resolutionNotes?: string
 ) => {
 const api = (await import('@/services/api/client')).default;
 await updateStatus(
 () => api.patch(`/api/sysadmin/error-log/${errorId}/status`, { status, resolution_notes: resolutionNotes }),
 {
 successMessage: t('sysAdmin.errorLog.saveStatusSuccess') || 'Status uppdaterad',
 errorMessage: t('common.updateStatusError'),
 onSuccess: () => {
 refetch();
 if (detailLog?.id === errorId) {
 setDetailLog(prev => prev ? { ...prev, status, resolution_notes: resolutionNotes ?? prev.resolution_notes } : null);
 }
 },
 }
 );
 }, [updateStatus, refetch, t, detailLog?.id]);

 const openDetail = useCallback((log: ErrorLogEntry) => {
 setDetailLog(log);
 setDetailStatus(log.status);
 setDetailResolutionNotes(log.resolution_notes ?? '');
 }, []);

 const closeDetail = useCallback(() => {
 setDetailLog(null);
 setDetailStatus('');
 setDetailResolutionNotes('');
 }, []);

 const handleSaveDetailStatus = useCallback(() => {
  if (!detailLog || !detailStatus) return;
  handleStatusUpdate(detailLog.id, detailStatus, detailResolutionNotes || undefined);
 }, [detailLog, detailStatus, detailResolutionNotes, handleStatusUpdate]);

 const handleClearLog = useCallback(async () => {
  setClearing(true);
  try {
   const api = (await import('@/services/api/client')).default;
   const params = clearOlderThan ? `?olderThanDays=${clearOlderThan}` : '';
   const response = await api.delete(`/api/sysadmin/error-log${params}`);
   const deletedCount = response.data?.data?.deletedCount ?? 0;
   setShowClearDialog(false);
   setClearOlderThan('');
   refetch();
   alert(t('sysAdmin.errorLog.deletedSuccess').replace('{count}', String(deletedCount)));
  } catch (err: any) {
   alert(t('sysAdmin.errorLog.deletedError') + ': ' + (err?.response?.data?.error ?? err.message));
  } finally {
   setClearing(false);
  }
 }, [clearOlderThan, refetch]);

 const getSeverityBadgeClass = (severity: string) => {
 const classes: Record<string, string> = {
 CRITICAL: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
 HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
 MEDIUM: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
 LOW: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 };
 return classes[severity] || 'bg-bgBase text-textDefault';
 };

 const getStatusBadgeClass = (status: string) => {
 const classes: Record<string, string> = {
 RESOLVED: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
 INVESTIGATING: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
 IGNORED: 'bg-bgBase text-textDefault',
 NEW: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
 };
 return classes[status] || classes.NEW;
 };

 const formatDate = (dateString: string) => {
 return new Date(dateString).toLocaleString('sv-SE', {
 year: 'numeric',
 month: '2-digit',
 day: '2-digit',
 hour: '2-digit',
 minute: '2-digit',
 second: '2-digit',
 });
 };

 return (
 <div className="p-6 max-w-7xl mx-auto">
 <div className="mb-6">
 <div className="flex items-center justify-between gap-3">
  <div className="flex items-center gap-3">
   <button onClick={() => navigate(ROUTES.SYSADMIN)} className="p-2 hover:bg-bgElevated rounded-lg transition-colors">
    <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
   </button>
   <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
    <ExclamationCircleIcon className="w-6 h-6 text-primary" />
    {t('sysAdmin.errorLog.title')}
   </h1>
  </div>
  <button
   type="button"
   onClick={() => setShowClearDialog(true)}
   className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
  >
   <TrashIcon className="w-4 h-4" />
   {t('sysAdmin.errorLog.clearLog')}
  </button>
 </div>
 <p className="mt-2 text-sm text-textSecondary">
  {t('sysAdmin.errorLog.description')}
 </p>
 </div>

 {/* Statistik */}
 {statistics && (
 <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">{t('sysAdmin.errorLog.totalLabel')}</div>
 <div className="text-2xl font-bold text-textDefault">{statistics.total}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">CRITICAL</div>
 <div className="text-2xl font-bold text-red-600">{statistics.bySeverity?.CRITICAL || 0}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">HIGH</div>
 <div className="text-2xl font-bold text-orange-600">{statistics.bySeverity?.HIGH || 0}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">NEW</div>
 <div className="text-2xl font-bold text-yellow-600">{statistics.byStatus?.NEW || 0}</div>
 </div>
 <div className="bg-bgElevated p-4 rounded-lg shadow">
 <div className="text-sm text-textSecondary">RESOLVED</div>
 <div className="text-2xl font-bold text-green-600">{statistics.byStatus?.RESOLVED || 0}</div>
 </div>
 </div>
 )}

 {/* Filter */}
 <div className="mb-6 bg-bgElevated p-4 rounded-lg shadow">
 <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('sysAdmin.errorLog.severityLabel')}</label>
 <select
 value={filters.severity || ''}
 onChange={(e) => handleFilterChange('severity', e.target.value || undefined)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 >
 <option value="">{t('common.all')}</option>
 <option value="LOW">LOW</option>
 <option value="MEDIUM">MEDIUM</option>
 <option value="HIGH">HIGH</option>
 <option value="CRITICAL">CRITICAL</option>
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('sysAdmin.errorLog.statusLabel')}</label>
 <select
 value={filters.status || ''}
 onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 >
 <option value="">{t('common.all')}</option>
 <option value="NEW">NEW</option>
 <option value="INVESTIGATING">INVESTIGATING</option>
 <option value="RESOLVED">RESOLVED</option>
 <option value="IGNORED">IGNORED</option>
 </select>
 </div>
 <div>
 <label className="block text-sm font-medium text-textDefault mb-1">{t('sysAdmin.errorLog.searchLabel')}</label>
 <input
 type="text"
 value={filters.search || ''}
 onChange={(e) => handleFilterChange('search', e.target.value)}
 placeholder={t('sysAdmin.errorLog.searchPlaceholder')}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 />
 </div>
 <div className="flex items-end">
 <button
 onClick={() => {
 setFilters({ severity: undefined, status: undefined, search: '' });
 pagination.firstPage();
 }}
 className="w-full px-4 py-2 bg-bgBase text-textDefault rounded-md hover:bg-bgBase"
 >
 {t('sysAdmin.errorLog.clearFilters')}
 </button>
 </div>
 </div>
 </div>

 {/* Lista med rubrik per rad */}
 <div className="bg-bgElevated rounded-lg shadow overflow-hidden">
 {loading ? (
 <div className="p-6 text-center text-textSecondary">{t('sysAdmin.errorLog.loading')}</div>
 ) : logs.length === 0 ? (
 <div className="p-6 text-center text-textSecondary space-y-2">
 <p>{t('sysAdmin.errorLog.noErrorsFound')}</p>
 {(filters.severity || filters.status || (filters.search && filters.search.trim())) && (
 <>
 <p className="text-sm text-textSecondary">
 {t('sysAdmin.errorLog.noResultsMatchFilters')}
 </p>
 <button
 type="button"
 onClick={() => { setFilters({ severity: undefined, status: undefined, search: '' }); pagination.firstPage(); }}
 className="mt-2 px-4 py-2 bg-bgPrimary text-textLight rounded-md hover:bg-bgSecondary"
 >
 {t('sysAdmin.errorLog.clearFilters')}
 </button>
 </>
 )}
 </div>
 ) : (
 <>
 <ul className="divide-y divide-border">
 {logs.map((log) => (
 <li key={log.id}>
 <button
 type="button"
 onClick={() => openDetail(log)}
 className="w-full text-left px-6 py-4 hover:bg-bgBase transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-border"
 >
 <div className="font-medium text-textDefault">
 {getErrorTitle(log.error_message)}
 </div>
 <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-textSecondary">
 <span>{formatDate(log.created_at)}</span>
 {log.error_type && <span>{log.error_type}</span>}
 {log.error_code && <span className="text-xs">{log.error_code}</span>}
 <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getSeverityBadgeClass(log.severity)}`}>
 {log.severity}
 </span>
 <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusBadgeClass(log.status)}`}>
 {log.status}
 </span>
 </div>
 </button>
 </li>
 ))}
 </ul>

 {pagination.total > pagination.pageSize && (
 <div className="px-6 py-4 border-t border-border flex items-center justify-between">
 <div className="text-sm text-textDefault">
 {t('sysAdmin.errorLog.showing').replace('{from}', String(pagination.startIndex + 1)).replace('{to}', String(pagination.endIndex)).replace('{total}', String(pagination.total))}
 </div>
 <div className="flex gap-2">
 <button
 onClick={pagination.previousPage}
 disabled={!pagination.hasPrevious}
 className="px-4 py-2 bg-bgBase text-textDefault rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bgBase"
 >
 {t('sysAdmin.errorLog.previous')}
 </button>
 <button
 onClick={pagination.nextPage}
 disabled={!pagination.hasNext}
 className="px-4 py-2 bg-bgBase text-textDefault rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-bgBase"
 >
 {t('sysAdmin.errorLog.next')}
 </button>
 </div>
 </div>
 )}
 </>
 )}
 </div>

 {/* Modal: bekräftelse för rensning av felloggen */}
 {showClearDialog && (
  <>
   <div className="fixed inset-0 z-40 bg-black/50" aria-hidden onClick={() => !clearing && setShowClearDialog(false)} />
   <div className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-bgElevated rounded-lg shadow-xl p-6">
    <h2 className="text-lg font-semibold text-textDefault mb-2 flex items-center gap-2">
     <TrashIcon className="w-5 h-5 text-red-600" />
     {t('sysAdmin.errorLog.clearLogTitle')}
    </h2>
    <p className="text-sm text-textSecondary mb-4">
     {t('sysAdmin.errorLog.clearLogDescription')}
    </p>
    <div className="mb-4">
     <label className="block text-sm font-medium text-textDefault mb-1">
      {t('sysAdmin.errorLog.clearOlderThanLabel')}
     </label>
     <input
      type="number"
      min="1"
      value={clearOlderThan}
      onChange={(e) => setClearOlderThan(e.target.value)}
      placeholder={t('sysAdmin.errorLog.clearOlderThanPlaceholder')}
      className="w-full px-3 py-2 border border-border rounded-md bg-bgBase text-textDefault"
     />
     <p className="text-xs text-textSecondary mt-1">
      {clearOlderThan
       ? t('sysAdmin.errorLog.clearOlderThanHint').replace('{days}', clearOlderThan)
       : t('sysAdmin.errorLog.clearAllHint')}
     </p>
    </div>
    <div className="flex justify-end gap-3">
     <button
      type="button"
      onClick={() => { setShowClearDialog(false); setClearOlderThan(''); }}
      disabled={clearing}
      className="px-4 py-2 rounded-md text-sm text-textDefault bg-bgBase hover:bg-border disabled:opacity-50"
     >
      {t('common.cancel')}
     </button>
     <button
      type="button"
      onClick={handleClearLog}
      disabled={clearing}
      className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
     >
      <TrashIcon className="w-4 h-4" />
      {clearing
       ? t('sysAdmin.errorLog.deleting')
       : clearOlderThan
       ? t('sysAdmin.errorLog.deleteOlderThan').replace('{days}', clearOlderThan)
       : t('sysAdmin.errorLog.deleteAll')}
     </button>
    </div>
   </div>
  </>
 )}

 {/* Modal: detaljvy för ett fel */}
 {detailLog && (
 <>
 <div
 className="fixed inset-0 z-40 bg-black/50"
 aria-hidden
 onClick={closeDetail}
 />
 <div className="fixed inset-4 z-50 overflow-hidden rounded-lg bg-bgElevated shadow-xl flex flex-col max-w-4xl mx-auto my-8">
 <div className="flex items-center justify-between px-6 py-4 border-b border-border">
 <h2 className="text-lg font-semibold text-textDefault">
 {t('sysAdmin.errorLog.viewDetails')}
 </h2>
 <button
 type="button"
 onClick={closeDetail}
 className="p-2 rounded-md text-textSecondary hover:text-textDefault hover:bg-bgBase"
 aria-label={t('sysAdmin.errorLog.close')}
 >
 <span className="sr-only">{t('sysAdmin.errorLog.close')}</span>
 <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 </button>
 </div>
 <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
 <div>
 <h3 className="text-sm font-medium text-textSecondary mb-1">{t('sysAdmin.errorLog.messageLabel')}</h3>
 <pre className="text-sm text-textDefault whitespace-pre-wrap break-words bg-bgLight p-3 rounded-md max-h-48 overflow-auto">
 {detailLog.error_message}
 </pre>
 </div>
 {detailLog.error_stack && (
 <div>
 <h3 className="text-sm font-medium text-textSecondary mb-1">Stack trace</h3>
 <pre className="text-xs text-textDefault whitespace-pre-wrap break-words bg-bgLight p-3 rounded-md max-h-64 overflow-auto">
 {detailLog.error_stack}
 </pre>
 </div>
 )}
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
 <div><span className="text-textSecondary">{t('sysAdmin.errorLog.errorTypeLabel')}:</span> {detailLog.error_type}</div>
 {detailLog.error_code && <div><span className="text-textSecondary">{t('sysAdmin.errorLog.errorCodeLabel')}:</span> {detailLog.error_code}</div>}
 <div><span className="text-textSecondary">Severity:</span> {detailLog.severity}</div>
 <div><span className="text-textSecondary">{t('sysAdmin.errorLog.status')}:</span> {detailLog.status}</div>
 {detailLog.service && <div><span className="text-textSecondary">{t('sysAdmin.errorLog.serviceLabel')}:</span> {detailLog.service}</div>}
 {detailLog.module && <div><span className="text-textSecondary">{t('sysAdmin.errorLog.moduleLabel')}:</span> {detailLog.module}</div>}
 {detailLog.function_name && <div><span className="text-textSecondary">{t('sysAdmin.errorLog.functionLabel')}:</span> {detailLog.function_name}</div>}
 <div><span className="text-textSecondary">{t('sysAdmin.errorLog.timeLabel')}:</span> {formatDate(detailLog.created_at)}</div>
 {detailLog.request_method && <div><span className="text-textSecondary">{t('sysAdmin.errorLog.requestLabel')}:</span> {detailLog.request_method} {detailLog.request_url}</div>}
 </div>
 {detailLog.request_body && (
 <div>
 <h3 className="text-sm font-medium text-textSecondary mb-1">Request body</h3>
 <pre className="text-xs text-textDefault whitespace-pre-wrap break-words bg-bgLight p-3 rounded-md max-h-32 overflow-auto">
 {detailLog.request_body}
 </pre>
 </div>
 )}
 {(detailLog.resolved_at || detailLog.resolution_notes) && (
 <div>
 {detailLog.resolved_at && <p className="text-sm text-textSecondary">{t('sysAdmin.errorLog.resolvedLabel')}: {formatDate(detailLog.resolved_at)}</p>}
 {detailLog.resolution_notes && (
 <p className="text-sm text-textDefault mt-1">{detailLog.resolution_notes}</p>
 )}
 </div>
 )}
 <div className="border-t border-border pt-4 space-y-3">
 <label className="block text-sm font-medium text-textDefault">
 {t('sysAdmin.errorLog.status')}
 </label>
 <select
 value={detailStatus}
 onChange={(e) => setDetailStatus(e.target.value as typeof detailStatus)}
 className="w-full max-w-xs px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 >
 <option value="NEW">NEW</option>
 <option value="INVESTIGATING">INVESTIGATING</option>
 <option value="RESOLVED">RESOLVED</option>
 <option value="IGNORED">IGNORED</option>
 </select>
 <label className="block text-sm font-medium text-textDefault">
 {t('sysAdmin.errorLog.resolutionNotes')}
 </label>
 <textarea
 value={detailResolutionNotes}
 onChange={(e) => setDetailResolutionNotes(e.target.value)}
 rows={3}
 className="w-full px-3 py-2 border border-border rounded-md bg-bgElevated text-textDefault"
 placeholder={t('sysAdmin.errorLog.resolutionNotesPlaceholder')}
 />
 <button
 type="button"
 onClick={handleSaveDetailStatus}
 disabled={!detailStatus}
 className="px-4 py-2 bg-bgPrimary text-textLight rounded-md hover:bg-bgSecondary disabled:opacity-50 disabled:cursor-not-allowed"
 >
 {t('sysAdmin.errorLog.saveStatus')}
 </button>
 </div>
 </div>
 </div>
 </>
 )}
 </div>
 );
};

export default ErrorLog;
